import {
	getCompletedCycleLogs,
	getAllCycleLogs,
	getMostActiveDevices,
	getMostActiveDevicesForAllSites,
	getMostActiveUsers,
	getMostActiveUsersForAllSites,
	getMostCleanedLocations,
	getMostCleanedLocationsForAllSites,
	getAllCycleLogsBelowCurrentLevel,
} from '@/api/dashboard';
import {
	GetCompletedCyclesQueryParams,
	GetAllCyclesQueryParams,
	MostActiveDevicesQueryParams,
	MostActiveUsersQueryParams,
	MostCleanedLocationsQueryParams,
} from '@/api/dashboard/types';
import { useQuery } from '@tanstack/react-query';

export const ALL_SITES_ID = 'all-sites';
export const ALL_CUSTOMERS_ID = 'all-customers';

export const useMostActiveUsers = (queryParams: MostActiveUsersQueryParams, siteId?: string) => {
	const queryKey = ['most_active_users', siteId, queryParams];
	const query = useQuery({
		queryKey,
		queryFn: () =>
			siteId === ALL_SITES_ID
				? getMostActiveUsersForAllSites(queryParams)
				: getMostActiveUsers(siteId!, queryParams),
		enabled: !!siteId && !!queryParams.fromDate && !!queryParams.toDate,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	return { queryKey, query };
};

export const useMostCleanedLocations = (
	queryParams: MostCleanedLocationsQueryParams,
	siteId?: string
) => {
	const queryKey = ['most_cleaned_locations', siteId, queryParams];
	const query = useQuery({
		queryKey,
		queryFn: () =>
			siteId === ALL_SITES_ID
				? getMostCleanedLocationsForAllSites(queryParams)
				: getMostCleanedLocations(siteId!, queryParams),
		enabled: !!siteId && !!queryParams.fromDate && !!queryParams.toDate,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	return { queryKey, query };
};

export const useCompletedCycles = (queryParams: GetCompletedCyclesQueryParams, siteId?: string) =>
	useQuery({
		queryKey: ['completed_cycles', siteId, queryParams],
		queryFn: () =>
			siteId === ALL_SITES_ID
				? getAllCycleLogs(queryParams)
				: getCompletedCycleLogs(siteId!, queryParams),
		enabled: !!siteId && !!queryParams.fromDate && !!queryParams.toDate,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
		retry: false,
	});

export const useMostActiveDevices = (
	queryParams: MostActiveDevicesQueryParams,
	siteId?: string
) => {
	const queryKey = ['most_active_devices', siteId, queryParams];
	const query = useQuery({
		queryKey,
		queryFn: () =>
			siteId === ALL_SITES_ID
				? getMostActiveDevicesForAllSites(queryParams)
				: getMostActiveDevices(siteId!, queryParams),
		enabled: !!siteId && !!queryParams.fromDate && !!queryParams.toDate,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
	});

	return { queryKey, query };
};

export const useAllCycles = (queryParams: GetAllCyclesQueryParams, siteId?: string) =>
	useQuery({
		queryKey: ['cycles_by_unit', siteId, queryParams],
		queryFn: () => getAllCycleLogsBelowCurrentLevel(queryParams),
		enabled: !!siteId && !!queryParams.fromDate && !!queryParams.toDate,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		retry: false,
	});
